import { Box, Container, Stack, Typography } from '@mui/material';
import { OChainId, TChainId } from 'modules/api';
import { useConnection } from 'modules/auth';
import { Button } from 'modules/common/components/Button';
import { NetworkIcon } from 'modules/common/components/NetworkIcon';
import { Section } from 'modules/common/components/Section';
import { useTranslation } from 'modules/i18n';
import { translation } from './translation';
import { useConnectableChainName } from './hooks/useConnectableChainName';
import { ScreenCardContent } from 'modules/withdrawal/components/Withdraw/components/ScreenCard/ScreenCardContent';

interface IWrongNetworkProps {
  expectedChainIds?: TChainId[];
}

export function WrongNetwork({
  expectedChainIds = [OChainId.ethereum],
}: IWrongNetworkProps): JSX.Element {
  const { keys, t } = useTranslation(translation);
  const {
    chainId = OChainId.unsupported,
    switchNetwork,
    isSwitchNetworkLoading,
  } = useConnection();
  const { getConnectableChainName } = useConnectableChainName();

  const isKnownChain = Object.values(OChainId).includes(chainId as TChainId);

  const handleSwitchNetwork = (chainId: TChainId) => {
    switchNetwork(chainId);
  };

  return (
    <Section>
      <Container maxWidth="xs">
        <ScreenCardContent>
          <Stack alignItems="center" justifyContent="center" spacing={2}>
            <Typography variant="h6">{t(keys.title)}</Typography>

            <Box sx={{ textAlign: 'center', mb: '40px' }}>
              <Typography
                variant="body2"
                sx={{
                  mb: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {t(keys.thisFeatureIsNotAvailable)}
                {isKnownChain && (
                  <NetworkIcon
                    chainId={chainId}
                    sx={{ fontSize: 28, mx: 0.5 }}
                  />
                )}
                {getConnectableChainName(
                  isKnownChain ? chainId : OChainId.unsupported,
                )}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {expectedChainIds.length > 1 ? (
                  t(keys.pleaseSwitchToSupported)
                ) : (
                  <>
                    {t(keys.pleaseSwitchTo)}{' '}
                    <NetworkIcon
                      chainId={expectedChainIds[0]}
                      sx={{ fontSize: 28, mx: 1.25 }}
                    />
                    {getConnectableChainName(expectedChainIds[0])}
                  </>
                )}
              </Typography>
            </Box>

            {expectedChainIds.map(expectedChainId => (
              <Button
                key={expectedChainId}
                fullWidth
                variant="contained"
                size="large"
                onClick={() => handleSwitchNetwork(expectedChainId)}
                isLoading={isSwitchNetworkLoading}
                disabled={isSwitchNetworkLoading}
                endIcon={
                  <NetworkIcon
                    chainId={expectedChainId}
                    sx={{ fontSize: 28 }}
                  />
                }
              >
                {t(keys.switchTo, {
                  chain: getConnectableChainName(expectedChainId),
                })}
              </Button>
            ))}
          </Stack>
        </ScreenCardContent>
      </Container>
    </Section>
  );
}
